import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  buildRsBookingTimeSelectOptions,
  ceilRsLocalDateTimeToBookingSlot,
  clampRsLocalDateTimeToMin,
  getRsMobileSubmitBarInsetPx,
  formatRsCalendarMonthTitle,
  formatRsLocalDateDisplay,
  formatRsLocalDateYmd,
  getRsDaysInMonth,
  joinRsLocalDateTime,
  parseRsLocalDateYmd,
  resolveRsDatePopoverCoords,
  shiftRsCalendarMonth,
  splitRsLocalDateTime,
} from '../../utils/reservationBoardCalendar.util';
import styles from './ReservationBoardCalendar.module.css';

const RS_DATE_POPOVER_WIDTH_PX = 15.5 * 16;
const RS_DATE_POPOVER_FALLBACK_HEIGHT_PX = 280;

type RsDateTimeLocalInputProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  locale?: string;
  required?: boolean;
  includeOvernightContinuation?: boolean;
  'aria-label'?: string;
};

type PopoverCoords = { top: number; left: number };

/**
 * OS の datetime-local / type=date は使わない。
 * 日付は自前ミニカレンダー、時刻は 30 分枠セレクト。
 */
export default function RsDateTimeLocalInput({
  id,
  value,
  onChange,
  min,
  locale,
  required = false,
  includeOvernightContinuation = false,
  'aria-label': ariaLabel,
}: RsDateTimeLocalInputProps) {
  const { t, i18n } = useTranslation();
  const uiLocale = locale || i18n.language || 'ja';
  const overnightPrefix = t('reservation.board.overnightPrefix', { defaultValue: '翌' });
  const popoverId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const displayRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<PopoverCoords | null>(null);

  const slotted = ceilRsLocalDateTimeToBookingSlot(value);
  const { date, time } = splitRsLocalDateTime(slotted);
  const minParts = splitRsLocalDateTime(min ? ceilRsLocalDateTimeToBookingSlot(min) : '');
  const dateDisplay = formatRsLocalDateDisplay(date);
  const selectedParts = parseRsLocalDateYmd(date);

  const [viewYear, setViewYear] = useState(() => selectedParts?.year ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => selectedParts?.month ?? new Date().getMonth() + 1);
  const wasOpenRef = useRef(false);

  const timeOptions = useMemo(
    () =>
      buildRsBookingTimeSelectOptions({
        selectedDate: date,
        minLocalDateTime: min,
        overnightPrefix,
        includeOvernightContinuation,
      }),
    [date, min, overnightPrefix, includeOvernightContinuation]
  );

  const weekdayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(uiLocale, { weekday: 'narrow' });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2021, 7, 1 + i)));
  }, [uiLocale]);

  const daysInView = getRsDaysInMonth(viewYear, viewMonth);
  const leadingBlanks = new Date(viewYear, viewMonth - 1, 1).getDay();
  const monthTitle = formatRsCalendarMonthTitle(uiLocale, viewYear, viewMonth);

  const emit = (nextDate: string, nextTime: string) => {
    if (!nextDate || !nextTime) return;
    const next = clampRsLocalDateTimeToMin(joinRsLocalDateTime(nextDate, nextTime), min);
    if (next !== value) onChange(next);
  };

  useEffect(() => {
    const currentValue = date && time ? joinRsLocalDateTime(date, time) : '';
    if (!currentValue || timeOptions.length === 0 || timeOptions.some((opt) => opt.value === currentValue)) {
      return;
    }
    const first = timeOptions[0];
    emit(first.date, first.time);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value 同期のみ
  }, [date, time, timeOptions]);

  // 開いた瞬間だけ選択日の月へ揃える（view 変更のたびに戻すと月送りが即キャンセルされる）
  useLayoutEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      setCoords(null);
      return;
    }
    if (!wasOpenRef.current && selectedParts) {
      setViewYear(selectedParts.year);
      setViewMonth(selectedParts.month);
    }
    wasOpenRef.current = true;
  }, [open, selectedParts?.year, selectedParts?.month]);

  useLayoutEffect(() => {
    if (!open) return;
    const sync = () => {
      const el = displayRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const height =
        popoverRef.current?.getBoundingClientRect().height || RS_DATE_POPOVER_FALLBACK_HEIGHT_PX;
      // 狭い画面の固定登録バーと重ならないよう下端 inset を引く
      const submitFixed = getRsMobileSubmitBarInsetPx();
      setCoords(
        resolveRsDatePopoverCoords(
          rect,
          { width: RS_DATE_POPOVER_WIDTH_PX, height },
          { width: window.innerWidth, height: window.innerHeight },
          8,
          4,
          submitFixed
        )
      );
    };

    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
    };
  }, [open, viewYear, viewMonth]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const currentSelectValue = date && time ? joinRsLocalDateTime(date, time) : '';
  const selectValue = timeOptions.some((opt) => opt.value === currentSelectValue)
    ? currentSelectValue
    : timeOptions[0]?.value ?? currentSelectValue;
  const timeLabel = t('reservation.board.timePart', { defaultValue: '時刻' });
  const todayYmd = formatRsLocalDateYmd(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    new Date().getDate()
  );
  const todayDisabled = Boolean(minParts.date && todayYmd < minParts.date);

  const pickDate = (nextDate: string) => {
    let nextTime = time || '09:00';
    if (minParts.date && nextDate === minParts.date && nextTime < minParts.time) {
      nextTime = minParts.time;
    }
    emit(nextDate, nextTime);
    setOpen(false);
    requestAnimationFrame(() => displayRef.current?.focus());
  };

  const shiftView = (delta: number) => {
    const next = shiftRsCalendarMonth(viewYear, viewMonth, delta);
    setViewYear(next.year);
    setViewMonth(next.month);
  };

  const popover =
    open && coords
      ? createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            className={styles.dateTimeDatePopover}
            role="dialog"
            aria-label={t('reservation.board.pickDate', { defaultValue: '日付を選ぶ' })}
            style={{ top: coords.top, left: coords.left }}
          >
            <div className={styles.dateTimeDatePopoverHead}>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className={styles.dateTimeDatePopoverNav}
                onClick={() => shiftView(-1)}
                aria-label={t('reservation.board.prevMonth', { defaultValue: '前月' })}
              >
                ‹
              </Button>
              <span className={styles.dateTimeDatePopoverTitle}>{monthTitle}</span>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className={styles.dateTimeDatePopoverNav}
                onClick={() => shiftView(1)}
                aria-label={t('reservation.board.nextMonth', { defaultValue: '次月' })}
              >
                ›
              </Button>
            </div>
            <div className={styles.dateTimeDatePopoverWeekdays} aria-hidden>
              {weekdayLabels.map((label, index) => (
                <span key={`wd-${index}`}>{label}</span>
              ))}
            </div>
            <div className={styles.dateTimeDatePopoverGrid}>
              {Array.from({ length: leadingBlanks }, (_, i) => (
                <span key={`pad-${i}`} className={styles.dateTimeDatePopoverPad} />
              ))}
              {daysInView.map((day) => {
                const ymd = formatRsLocalDateYmd(viewYear, viewMonth, day);
                const disabled = Boolean(minParts.date && ymd < minParts.date);
                const selected = ymd === date;
                const isToday = ymd === todayYmd;
                return (
                  <button
                    key={ymd}
                    type="button"
                    disabled={disabled}
                    className={`${styles.dateTimeDatePopoverDay}${
                      selected ? ` ${styles.dateTimeDatePopoverDaySelected}` : ''
                    }${isToday ? ` ${styles.dateTimeDatePopoverDayToday}` : ''}`}
                    onClick={() => pickDate(ymd)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <div className={styles.dateTimeDatePopoverFoot}>
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                disabled={todayDisabled}
                onClick={() => pickDate(todayYmd)}
              >
                {t('reservation.board.today', { defaultValue: '今日' })}
              </Button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={styles.dateTimeParts} ref={rootRef}>
      <div className={styles.dateTimeDateWrap}>
        <Form.Control
          ref={displayRef}
          id={id}
          type="text"
          readOnly
          size="sm"
          value={dateDisplay}
          title={dateDisplay}
          aria-label={ariaLabel ? `${ariaLabel} ${dateDisplay}` : dateDisplay}
          aria-expanded={open}
          aria-controls={popoverId}
          aria-haspopup="dialog"
          aria-required={required || undefined}
          className={styles.dateTimeLocalDisplay}
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((prev) => !prev);
            }
          }}
        />
        <i className={`fas fa-calendar-alt ${styles.dateTimeLocalIcon}`} aria-hidden />
      </div>
      <Form.Select
        size="sm"
        value={selectValue}
        onChange={(e) => {
          const opt = timeOptions.find((o) => o.value === e.target.value);
          if (opt) emit(opt.date, opt.time);
        }}
        required={required}
        aria-label={ariaLabel ? `${ariaLabel} ${timeLabel}` : timeLabel}
        className={styles.dateTimeTimeSelect}
      >
        {timeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>
      {popover}
    </div>
  );
}
